import { useState } from "react";
import { useParams } from "react-router-dom";
import { useFetch } from "../lib/useFetch";
import { PageHeader, Panel, Stat, Loading, Chip } from "../components/common/ui";
import { SubTabs } from "../components/common/SubTabs";
import { Bars, Donut, AreaTrend } from "../components/common/Charts";
import { inr } from "../lib/format";
import { cn } from "../lib/utils";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../components/ui/sheet";
import { Input } from "../components/ui/input";

const TABS = [
  { key: "directory", label: "Customer 360" }, { key: "segments", label: "Segments" },
  { key: "retention", label: "Retention" }, { key: "churn", label: "Churn" },
];

const segColor = (s) => ({
  VIP: "bg-primary/10 text-primary", "High Value": "bg-secondary/15 text-emerald-700",
  Frequent: "bg-blue-100 text-blue-700", New: "bg-slate-100 text-slate-600",
  "At Risk": "bg-amber-100 text-amber-700", Dormant: "bg-slate-200 text-slate-600",
  "Complaint Prone": "bg-red-100 text-red-700", "Discount Dependent": "bg-orange-100 text-orange-700",
}[s] || "bg-slate-100 text-slate-600");

export default function CustomerIntelligence() {
  const { tab = "directory" } = useParams();
  return (
    <div data-testid="customer-page">
      <PageHeader title="Customer Intelligence" subtitle="Customer 360, segmentation, retention and AI churn prediction." />
      <SubTabs base="/customers" tab={tab} items={TABS} />
      {tab === "directory" && <Directory />}
      {tab === "segments" && <Segments />}
      {tab === "retention" && <Retention />}
      {tab === "churn" && <Churn />}
    </div>
  );
}

function Directory() {
  const [search, setSearch] = useState("");
  const [seg, setSeg] = useState("all");
  const { data, loading } = useFetch(`/customers?search=${encodeURIComponent(search)}&segment=${seg}`, [search, seg]);
  const [active, setActive] = useState(null);
  const segs = ["all", "VIP", "High Value", "Frequent", "New", "At Risk", "Dormant", "Complaint Prone", "Discount Dependent"];
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <Input placeholder="Search customers…" value={search} onChange={(e) => setSearch(e.target.value)} className="w-56 h-9" data-testid="customer-search" />
        <div className="flex flex-wrap gap-1">
          {segs.map((s) => (
            <button key={s} onClick={() => setSeg(s)} className={cn("px-2.5 py-1 rounded-full text-xs font-medium", seg === s ? "bg-primary text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200")}>
              {s === "all" ? "All" : s}
            </button>
          ))}
        </div>
      </div>
      <Panel>
        {loading || !data ? <Loading rows={5} /> : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Customer</TableHead><TableHead>Hub</TableHead><TableHead>Segment</TableHead>
                <TableHead className="text-right">Orders</TableHead><TableHead className="text-right">Revenue</TableHead>
                <TableHead className="text-right">AOV</TableHead><TableHead className="text-right">Rating</TableHead>
                <TableHead className="text-right">Churn</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.slice(0, 80).map((c) => (
                <TableRow key={c.id} onClick={() => setActive(c)} className="cursor-pointer hover:bg-slate-50" data-testid={`customer-row-${c.id}`}>
                  <TableCell className="font-medium">{c.name}<div className="text-xs text-slate-400">{c.phone}</div></TableCell>
                  <TableCell className="text-slate-500">{c.hub_name}</TableCell>
                  <TableCell><Chip className={segColor(c.segment)}>{c.segment}</Chip></TableCell>
                  <TableCell className="text-right">{c.orders}</TableCell>
                  <TableCell className="text-right">₹{inr(c.revenue)}</TableCell>
                  <TableCell className="text-right">₹{inr(c.aov)}</TableCell>
                  <TableCell className="text-right">{c.rating}</TableCell>
                  <TableCell className={cn("text-right font-medium", c.churn_score >= 60 ? "text-red-600" : "text-slate-500")}>{c.churn_score}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Panel>
      <Sheet open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto scrollbar-thin" data-testid="customer-drawer">
          {active && (
            <>
              <SheetHeader><SheetTitle className="font-heading">{active.name}</SheetTitle></SheetHeader>
              <Chip className={cn("mt-2", segColor(active.segment))}>{active.segment}</Chip>
              <div className="grid grid-cols-2 gap-2 mt-4">
                <Stat label="Orders" value={active.orders} /><Stat label="Revenue" value={"₹" + inr(active.revenue)} />
                <Stat label="AOV" value={"₹" + inr(active.aov)} /><Stat label="Frequency" value={`${active.frequency}/mo`} />
                <Stat label="Complaints" value={active.complaints} /><Stat label="Refunds" value={active.refunds} />
                <Stat label="Rating" value={active.rating} /><Stat label="Tickets" value={active.tickets} />
                <Stat label="Churn Score" value={active.churn_score} tone={active.churn_score >= 60 ? "text-red-600" : ""} />
                <Stat label="Support" value={active.support_interactions} />
              </div>
              <div className="mt-4 text-sm text-slate-600">
                <div className="text-xs font-semibold text-slate-400 uppercase mb-1">Services used</div>
                <div className="flex flex-wrap gap-1">{active.services.map((s) => <Chip key={s} className="bg-slate-100 text-slate-600">{s}</Chip>)}</div>
                <div className="mt-3 text-xs text-slate-400">First order {active.first_order} · Last order {active.last_order}</div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function Segments() {
  const { data, loading } = useFetch("/customers/segments", []);
  if (loading || !data) return <Loading rows={4} />;
  return (
    <div className="space-y-6">
      <div className="grid lg:grid-cols-2 gap-6">
        <Panel title="Customers by Segment"><Donut data={data.map((s) => ({ name: s.segment, value: s.count }))} height={320} /></Panel>
        <Panel title="Revenue by Segment"><Bars data={data.map((s) => ({ name: s.segment, value: s.revenue }))} horizontal currency height={320} /></Panel>
      </div>
      <Panel title="Segment Detail">
        <Table>
          <TableHeader><TableRow><TableHead>Segment</TableHead><TableHead className="text-right">Customers</TableHead><TableHead className="text-right">Revenue</TableHead><TableHead className="text-right">Avg AOV</TableHead><TableHead className="text-right">Avg Churn</TableHead></TableRow></TableHeader>
          <TableBody>
            {data.map((s) => (
              <TableRow key={s.segment} className="hover:bg-slate-50">
                <TableCell><Chip className={segColor(s.segment)}>{s.segment}</Chip></TableCell>
                <TableCell className="text-right">{s.count}</TableCell>
                <TableCell className="text-right">₹{inr(s.revenue)}</TableCell>
                <TableCell className="text-right">₹{inr(s.avg_aov)}</TableCell>
                <TableCell className={cn("text-right", s.avg_churn >= 60 ? "text-red-600" : "")}>{s.avg_churn}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Panel>
    </div>
  );
}

function Retention() {
  const { data, loading } = useFetch("/customers/retention", []);
  if (loading || !data) return <Loading rows={3} />;
  const tiles = [["New", data.new], ["Repeat", data.repeat], ["Retained", data.retained], ["Lost", data.lost], ["Reactivated", data.reactivated]];
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        {tiles.map(([l, v]) => <Stat key={l} label={l} value={v} />)}
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        <Stat label="Retention Rate" value={`${data.retention_rate}%`} tone="text-emerald-600" />
        <Stat label="Churn Rate" value={`${data.churn_rate}%`} tone="text-red-600" />
      </div>
      <Panel title="Retention Trend"><AreaTrend data={data.trend.map((m) => ({ date: m.month, value: m.retention }))} color="#88D317" height={280} /></Panel>
    </div>
  );
}

function Churn() {
  const { data, loading } = useFetch("/customers/churn", []);
  if (loading || !data) return <Loading rows={4} />;
  return (
    <div className="space-y-6">
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="kpi-num text-3xl font-extrabold text-red-600">{data.at_risk_count.toLocaleString("en-IN")}</div>
        <div className="text-sm text-red-700">customers have high churn probability (score ≥ 60)</div>
      </div>
      <div className="grid lg:grid-cols-2 gap-6">
        <Panel title="Churn Reasons"><Bars data={data.reasons.map((r) => ({ name: r.reason, value: r.count }))} horizontal height={280} /></Panel>
        <Panel title="Highest Risk Customers">
          <div className="space-y-2 max-h-[280px] overflow-y-auto scrollbar-thin">
            {data.customers.slice(0, 30).map((c) => (
              <div key={c.id} className="flex items-center justify-between text-sm border border-slate-100 rounded-md px-3 py-2">
                <div><span className="font-medium">{c.name}</span><span className="text-xs text-slate-400 ml-2">{c.hub_name}</span></div>
                <span className="font-semibold text-red-600">{c.churn_score}</span>
              </div>
            ))}
          </div>
        </Panel>
      </div>
    </div>
  );
}
